import React, { useState, useEffect, useCallback } from 'react';
import {
  FaUsers,
  FaUserCheck,
  FaUserTimes,
  FaUserTie,
  FaSearch,
  FaFilter,
  FaEye,
  FaEdit,
  FaBan,
  FaCheckCircle,
  FaSync,
  FaCalendarAlt,
  FaEnvelope,
  FaPhone,
  FaUserCircle,
  FaCrown,
  FaShieldAlt,
  FaUserShield,
  FaUser,
  FaTrash,
  FaKey,
  FaUserCog,
  FaHistory,
  FaShoppingCart,
  FaUtensils,
  FaRubleSign,
  FaHeart,
  FaCreditCard,
  FaCashRegister,
  FaHome,
  FaFileExport,
  FaLock,
  FaUnlock,
  FaCalculator
} from 'react-icons/fa';
import { adminApiService } from '../services/adminApi';

// Компонент Tooltip для подсказок при наведении
const Tooltip = ({ children, text, position = 'top' }) => {
  const [show, setShow] = useState(false);
  
  const positionClasses = {
    top: 'bottom-full left-1/2 transform -translate-x-1/2 mb-2',
    bottom: 'top-full left-1/2 transform -translate-x-1/2 mt-2',
    left: 'right-full top-1/2 transform -translate-y-1/2 mr-2',
    right: 'left-full top-1/2 transform -translate-y-1/2 ml-2'
  };

  return (
    <div 
      className="relative inline-block"
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      {children}
      {show && (
        <div className={`absolute z-50 px-2 py-1 text-xs text-white bg-gray-900 rounded shadow-lg whitespace-nowrap ${positionClasses[position]}`}>
          {text}
          <div className={`absolute w-2 h-2 bg-gray-900 transform rotate-45 ${
            position === 'top' ? 'top-full left-1/2 -translate-x-1/2 -mt-1' :
            position === 'bottom' ? 'bottom-full left-1/2 -translate-x-1/2 -mb-1' :
            position === 'left' ? 'left-full top-1/2 -translate-y-1/2 -ml-1' :
            'right-full top-1/2 -translate-y-1/2 -mr-1'
          }`}></div>
        </div>
      )}
    </div>
  );
};

const UsersManagement = () => {
  // Получаем текущего пользователя из localStorage
  const getCurrentUser = () => {
    try {
      const userStr = localStorage.getItem('user');
      return userStr ? JSON.parse(userStr) : null;
    } catch {
      return null;
    }
  };
  const currentUser = getCurrentUser();

  // Иерархия ролей (от высшей к низшей)
  // Примечание: "Клиент" не в этом списке - клиенты это обычные пользователи БЕЗ StaffProfile
  const roleHierarchy = {
    'developer': 7,  // Самый высокий уровень
    'admin': 6,
    'manager': 5,
    'accountant': 4,
    'chef': 3,
    'waiter': 2,
    'cashier': 1     // Самый низкий уровень сотрудников
  };

  // Получить уровень роли текущего пользователя
  const getCurrentUserRoleLevel = () => {
    if (currentUser?.staff_profile_role) {
      return roleHierarchy[currentUser.staff_profile_role] || 0;
    }
    // Fallback для старых полей
    if (currentUser?.is_developer || currentUser?.has_full_access) {
      return roleHierarchy['developer'];
    }
    if (currentUser?.is_staff) {
      return roleHierarchy['admin'];
    }
    return 0;
  };

  // Фильтрация доступных ролей: пользователь может выдавать только роли ниже своей
  // Примечание: "Клиент" здесь отсутствует - это обычные пользователи без StaffProfile
  const getAvailableRoles = () => {
    const currentLevel = getCurrentUserRoleLevel();
    const allRoles = [
      { value: 'developer', label: '👑 Developer - Выше всех', level: 7 },
      { value: 'admin', label: '🛡️ Администратор - Доступ к админ панели и кассе', level: 6 },
      { value: 'manager', label: '👔 Менеджер - Управление заведением', level: 5 },
      { value: 'accountant', label: '📊 Бухгалтер - Доступ к техпанели', level: 4 },
      { value: 'chef', label: '👨‍🍳 Повар - Кухня и заказы', level: 3 },
      { value: 'waiter', label: '🍽️ Официант - Обслуживание столов', level: 2 },
      { value: 'cashier', label: '💰 Кассир - Доступ к кассе', level: 1 }
    ];
    
    // Фильтруем роли: показываем только те, что ниже текущей роли пользователя
    return allRoles.filter(role => role.level < currentLevel);
  };

  // Проверка: может ли текущий пользователь редактировать роль целевого пользователя
  const canEditUserRole = (targetUser) => {
    const currentLevel = getCurrentUserRoleLevel();
    
    if (!targetUser?.staff_profile_role) {
      return true; // Если у пользователя нет роли, можно редактировать
    }
    
    const targetLevel = roleHierarchy[targetUser.staff_profile_role] || 0;
    
    // Можно редактировать только если роль целевого пользователя НИЖЕ текущей
    return targetLevel < currentLevel;
  };

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState('view'); // 'view', 'edit', 'create'
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [activeTab, setActiveTab] = useState('customers'); // 'customers' or 'staff'
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    staff: 0,
    superusers: 0,
    withAvatars: 0,
  });
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showCashierSettingsDialog, setShowCashierSettingsDialog] = useState(false);
  const [showCashierEditDialog, setShowCashierEditDialog] = useState(false);
  const [selectedUserForAction, setSelectedUserForAction] = useState(null);
  const [establishments, setEstablishments] = useState([]);

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page_size: 100,
        ...(searchTerm && { q: searchTerm }),
        ...(filterStatus !== 'all' && { is_active: filterStatus === 'active' }),
        ...(filterType !== 'all' && { user_type: filterType }),
      };
      
      const response = await adminApiService.getUsers(params);
      setUsers(response.results || []);
    } catch (error) {
      console.error('Ошибка загрузки пользователей:', error);
      showNotification('Ошибка загрузки пользователей', 'error');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, filterStatus, filterType]);

  const loadStats = async () => {
    try {
      const response = await adminApiService.getUsers({ page_size: 1000 });
      const allUsers = response.results || [];
      
      const getAvatarUrl = (user) => {
        if (user.profile_image) return user.profile_image;
        if (user.profile && user.profile.profile_image) return user.profile.profile_image;
        return null;
      };
      
      setStats({
        total: allUsers.length,
        active: allUsers.filter(u => u.is_active).length,
        inactive: allUsers.filter(u => !u.is_active).length,
        staff: allUsers.filter(u => u.is_staff).length,
        developers: allUsers.filter(u => u.is_developer || u.has_full_access).length,
        superusers: allUsers.filter(u => u.is_superuser).length,
        withAvatars: allUsers.filter(u => getAvatarUrl(u)).length,
      });
    } catch (error) {
      console.error('Ошибка загрузки статистики:', error);
    }
  };


  const loadEstablishments = async () => {
    try {
      console.log('🔍 Начинаем загрузку заведений...');
      const response = await adminApiService.getEstablishments();
      console.log('✅ Ответ от API getEstablishments:', response);
      console.log('📊 Тип ответа:', typeof response);
      console.log('📊 Является массивом?', Array.isArray(response));
      
      const establishmentsData = Array.isArray(response) ? response : (response.results || response || []);
      console.log('📦 Обработанные заведения:', establishmentsData);
      console.log('📊 Количество заведений:', establishmentsData.length);
      
      if (establishmentsData.length > 0) {
        console.log('📋 Первое заведение:', establishmentsData[0]);
      }
      
      setEstablishments(establishmentsData);
      console.log('✅ Заведения сохранены в state');
    } catch (error) {
      console.error('❌ Ошибка загрузки заведений:', error);
      console.error('❌ Детали ошибки:', error.response?.data);
      setEstablishments([]);
    }
  };

  useEffect(() => {
    loadUsers();
    loadStats();
    loadEstablishments();
  }, [loadUsers]);

  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: '', type: 'success' }), 5000);
  };

  const handleViewUser = (user) => {
    setSelectedUser(user);
    setDialogType('view');
    setOpenDialog(true);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setDialogType('edit');
    setOpenDialog(true);
  };

  const handleToggleUserStatus = async (user) => {
    try {
      const newStatus = !user.is_active;
      await adminApiService.toggleUserStatus(user.id, newStatus);
      showNotification(
        `Пользователь ${user.is_active ? 'заблокирован' : 'активирован'}`,
        'success'
      );
      loadUsers();
      loadStats();
    } catch (error) {
      console.error('Ошибка изменения статуса пользователя:', error);
      showNotification('Ошибка изменения статуса пользователя', 'error');
    }
  };

  const handleSaveUser = async (userData) => {
    try {
      console.log('Сохранение пользователя:', userData);
      
      let userId;
      
      if (dialogType === 'edit') {
        const response = await adminApiService.updateUser(selectedUser.id, userData);
        console.log('Ответ сервера при обновлении:', response);
        userId = selectedUser.id;
        showNotification('Пользователь обновлен', 'success');
      } else if (dialogType === 'create') {
        const response = await adminApiService.createUser(userData);
        console.log('Ответ сервера при создании:', response);
        userId = response.id;
        showNotification('Пользователь создан', 'success');
      }
      
      setOpenDialog(false);
      setShowCreateDialog(false);
      setSelectedUser(null);
      await loadUsers();
      await loadStats();
    } catch (error) {
      console.error('Ошибка сохранения пользователя:', error);
      console.error('Детали ошибки:', error.response?.data);
      
      // Показываем более детальную ошибку
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.detail ||
                          error.response?.data?.non_field_errors?.[0] ||
                          Object.values(error.response?.data || {}).flat().join(', ') ||
                          error.message ||
                          'Неизвестная ошибка';
      
      showNotification(
        `Ошибка сохранения: ${errorMessage}`, 
        'error'
      );
    }
  };

  const handleDeleteUser = async () => {
    try {
      await adminApiService.deleteUser(selectedUserForAction.id);
      showNotification('Пользователь удален', 'success');
      setShowDeleteDialog(false);
      setSelectedUserForAction(null);
      loadUsers();
      loadStats();
    } catch (error) {
      console.error('Ошибка удаления пользователя:', error);
      showNotification('Ошибка удаления пользователя', 'error');
    }
  };

  const handleResetPassword = async (newPassword) => {
    try {
      await adminApiService.resetUserPassword(selectedUserForAction.id, newPassword);
      showNotification('Пароль сброшен', 'success');
      setShowPasswordDialog(false);
      setSelectedUserForAction(null);
    } catch (error) {
      console.error('Ошибка сброса пароля:', error);
      showNotification('Ошибка сброса пароля', 'error');
    }
  };

  const handleToggleStaff = async (user) => {
    try {
      await adminApiService.toggleUserStaff(user.id, !user.is_staff);
      showNotification(
        `Права ${user.is_staff ? 'сняты' : 'назначены'}`,
        'success'
      );
      loadUsers();
      loadStats();
    } catch (error) {
      console.error('Ошибка изменения прав:', error);
      showNotification('Ошибка изменения прав', 'error');
    }
  };

  const handleToggleSuperuser = async (user) => {
    try {
      await adminApiService.toggleUserSuperuser(user.id, !user.is_superuser);
      showNotification(
        `Права суперпользователя ${user.is_superuser ? 'сняты' : 'назначены'}`,
        'success'
      );
      loadUsers();
      loadStats();
    } catch (error) {
      console.error('Ошибка изменения прав суперпользователя:', error);
      showNotification('Ошибка изменения прав суперпользователя', 'error');
    }
  };

  const handleToggleAccountant = async (user) => {
    try {
      await adminApiService.toggleAccountant(user.id);
      showNotification(
        `Права бухгалтера ${user.is_accountant ? 'сняты' : 'назначены'}`,
        'success'
      );
      loadUsers();
      loadStats();
    } catch (error) {
      console.error('Ошибка изменения прав бухгалтера:', error);
      showNotification('Ошибка изменения прав бухгалтера', 'error');
    }
  };

  const handleToggleCashier = async (user) => {
    try {
      if (user.is_cashier) {
        // Если кассир уже активен, просто отключаем
        await adminApiService.toggleCashier(user.id);
        showNotification('Права кассира сняты', 'success');
        loadUsers();
        loadStats();
      } else {
        // Если нужно активировать кассира, сначала загружаем заведения
        console.log('📥 Загружаем заведения перед открытием диалога...');
        await loadEstablishments(); // ВАЖНО: ждем загрузки заведений
        setSelectedUserForAction(user);
        setShowCashierSettingsDialog(true);
      }
    } catch (error) {
      console.error('Ошибка изменения прав кассира:', error);
      showNotification('Ошибка изменения прав кассира', 'error');
    }
  };

  const handleEditCashier = async (user) => {
    try {
      // Загружаем заведения и профиль кассира
      console.log('📥 Загружаем данные для редактирования кассира...');
      await loadEstablishments(); // ВАЖНО: ждем загрузки заведений
      const cashierProfile = await adminApiService.getCashierProfile(user.id);
      setSelectedUserForAction({ ...user, cashierProfile });
      setShowCashierEditDialog(true);
    } catch (error) {
      console.error('Ошибка загрузки профиля кассира:', error);
      showNotification('Ошибка загрузки профиля кассира', 'error');
    }
  };

  const handleSaveCashierSettings = async (cashierData) => {
    try {
      console.log('📤 Отправка данных кассира:', cashierData);
      
      // ✅ ИСПРАВЛЕНО: Сначала создаём профиль, ЗАТЕМ активируем флаг is_cashier
      // (иначе toggleCashier может снять флаг, если он уже был установлен)
      
      // 1. Создаем профиль кассира с настройками
      await adminApiService.createCashierProfile(selectedUserForAction.id, cashierData);
      
      // 2. Активируем флаг is_cashier (если ещё не активен)
      if (!selectedUserForAction.is_cashier) {
        await adminApiService.toggleCashier(selectedUserForAction.id);
      }
      
      showNotification(`Пользователь ${selectedUserForAction.username} назначен кассиром`, 'success');
      setShowCashierSettingsDialog(false);
      setSelectedUserForAction(null);
      loadUsers();
      loadStats();
    } catch (error) {
      console.error('❌ Ошибка создания профиля кассира:', error);
      console.error('📋 Детали ошибки:', error.response?.data);
      showNotification(`Ошибка: ${error.response?.data?.error || error.message}`, 'error');
    }
  };

  const handleUpdateCashierSettings = async (cashierData) => {
    try {
      // Обновляем профиль кассира
      await adminApiService.updateCashierProfile(selectedUserForAction.id, cashierData);
      
      showNotification(`Профиль кассира ${selectedUserForAction.username} обновлен`, 'success');
      setShowCashierEditDialog(false);
      setSelectedUserForAction(null);
      loadUsers();
      loadStats();
    } catch (error) {
      console.error('Ошибка обновления профиля кассира:', error);
      showNotification('Ошибка обновления профиля кассира', 'error');
    }
  };

  const handleExportUsers = async () => {
    try {
      const blob = await adminApiService.exportUsers('csv', {
        ...(searchTerm && { q: searchTerm }),
        ...(filterStatus !== 'all' && { is_active: filterStatus === 'active' }),
        ...(filterType !== 'all' && { user_type: filterType }),
      });
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `users_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      showNotification('Экспорт завершен', 'success');
    } catch (error) {
      console.error('Ошибка экспорта:', error);
      showNotification('Ошибка экспорта', 'error');
    }
  };

  // Функции форматирования
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getUserTypeText = (user) => {
    // Если есть роль из StaffProfile - показываем её (это сотрудник)
    if (user.staff_profile_role) {
      const roleMap = {
        'developer': 'Developer',
        'admin': 'Администратор',
        'manager': 'Менеджер',
        'waiter': 'Официант',
        'chef': 'Повар',
        'accountant': 'Бухгалтер',
        'cashier': 'Кассир'
      };
      return roleMap[user.staff_profile_role] || user.staff_profile_role;
    }
    
    // Если нет StaffProfile - это обычный клиент
    // (Старые флаги is_staff, is_accountant, is_cashier - legacy)
    if (user.is_staff) return 'Сотрудник';
    if (user.is_accountant) return 'Бухгалтер';
    if (user.is_cashier) return 'Кассир';
    return 'Клиент';  // По умолчанию - клиент
  };

  const getUserTypeBadge = (user) => {
    // Цвета по ролям из StaffProfile (для сотрудников)
    if (user.staff_profile_role) {
      const colorMap = {
        'developer': 'purple',
        'admin': 'danger',
        'manager': 'warning',
        'waiter': 'info',
        'chef': 'success',
        'accountant': 'primary',
        'cashier': 'success'
      };
      return colorMap[user.staff_profile_role] || 'info';
    }
    
    // Если нет StaffProfile - это клиент (серый цвет)
    // (Старые флаги - legacy)
    if (user.is_developer || user.has_full_access) return 'purple';
    if (user.is_superuser) return 'danger';
    if (user.is_staff) return 'warning';
    if (user.is_accountant) return 'info';
    if (user.is_cashier) return 'success';
    return 'secondary';  // Клиент - серый
  };

  const getUserTypeIcon = (user) => {
    // Роль из StaffProfile (для сотрудников)
    if (user.staff_profile_role) {
      const iconMap = {
        'developer': FaCrown,           // 👑 Корона для Developer
        'admin': FaUserShield,          // 🛡️ Щит для Администратора
        'manager': FaUserTie,           // 👔 Менеджер
        'waiter': FaUser,               // 🍽️ Официант
        'chef': FaUtensils,             // 👨‍🍳 Повар (столовые приборы)
        'accountant': FaCalculator,     // 📊 Бухгалтер
        'cashier': FaCashRegister       // 💰 Кассир
      };
      return iconMap[user.staff_profile_role] || FaUser;
    }
    
    // Если нет StaffProfile - это клиент (иконка пользователя)
    // (Старые флаги - legacy)
    if (user.is_staff) return FaUserTie;
    if (user.is_accountant) return FaCalculator;
    if (user.is_cashier) return FaCashRegister;
    return FaUser;  // 👤 Клиент
  };

  // Компонент аватара пользователя
  const UserAvatar = ({ user, size = 'md' }) => {
    const sizeClasses = {
      sm: 'w-8 h-8 text-sm',
      md: 'w-12 h-12 text-lg',
      lg: 'w-16 h-16 text-xl',
      xl: 'w-20 h-20 text-2xl'
    };

    const getInitials = (user) => {
      if (user.first_name && user.last_name) {
        return `${user.first_name.charAt(0)}${user.last_name.charAt(0)}`.toUpperCase();
      }
      if (user.first_name) {
        return user.first_name.charAt(0).toUpperCase();
      }
      return user.username?.charAt(0).toUpperCase() || user.email?.charAt(0).toUpperCase() || '?';
    };

    const getAvatarColor = (user) => {
      const colors = [
        'bg-blue-500', 'bg-green-500', 'bg-yellow-500', 'bg-red-500', 
        'bg-purple-500', 'bg-pink-500', 'bg-indigo-500', 'bg-teal-500'
      ];
      const index = (user.username?.charCodeAt(0) || 0) % colors.length;
      return colors[index];
    };

    const getAvatarUrl = (user) => {
      if (user.profile_image) {
        if (user.profile_image.startsWith('http')) {
          return user.profile_image;
        }
        return user.profile_image;
      }
      
      if (user.profile && user.profile.profile_image) {
        if (user.profile.profile_image.startsWith('http')) {
          return user.profile.profile_image;
        }
        return user.profile.profile_image;
      }
      
      return null;
    };

    const avatarUrl = getAvatarUrl(user);

    if (avatarUrl) {
      return (
        <img
          src={avatarUrl}
          alt={user.username}
          className={`${sizeClasses[size]} rounded-full object-cover border-2 border-white shadow-md`}
          onError={(e) => {
            e.target.style.display = 'none';
            e.target.nextElementSibling.style.display = 'flex';
          }}
        />
      );
    }

    return (
      <div className={`${sizeClasses[size]} ${getAvatarColor(user)} rounded-full flex items-center justify-center text-white font-semibold shadow-md border-2 border-white`}>
        {getInitials(user)}
      </div>
    );
  };

  // Компонент статус-бейджа
  const StatusBadge = ({ type, children, icon: Icon }) => {
    const getStatusColors = () => {
      switch (type) {
        case 'success':
          return 'bg-green-100 text-green-800 border-green-200';
        case 'warning':
          return 'bg-yellow-100 text-yellow-800 border-yellow-200';
        case 'danger':
          return 'bg-red-100 text-red-800 border-red-200';
        case 'info':
          return 'bg-blue-100 text-blue-800 border-blue-200';
        default:
          return 'bg-gray-100 text-gray-800 border-gray-200';
      }
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColors()}`}>
        {Icon && <Icon className="w-3 h-3 mr-1" />}
        {children}
      </span>
    );
  };

  // Компонент диалога пользователя
  const UserDialog = ({ open, onClose, user, type, onSave, establishments = [] }) => {
    const [formData, setFormData] = useState({
      username: user?.username || '',
      email: user?.email || '',
      first_name: user?.first_name || '',
      phone: user?.phone || '',
      password: '',
      password_confirm: '',
      is_active: user?.is_active !== undefined ? user.is_active : true,
      is_staff: user?.is_staff || false,
      is_accountant: user?.is_accountant || false,
      is_cashier: user?.is_cashier || false,
      is_developer: user?.is_developer || user?.has_full_access || false,
      is_superuser: user?.is_superuser || false,
      staff_role: user?.staff_profile_role || '', // Роль из StaffProfile
      // Поля для кассира
      establishment: '',
      location: '',
      employee_id: '',
      hired_date: new Date().toISOString().split('T')[0],
    });

    const [locations, setLocations] = useState([]);

    // Сброс формы при изменении пользователя
    useEffect(() => {
      if (user) {
        setFormData({
          username: user.username || '',
          email: user.email || '',
          first_name: user.first_name || '',
          phone: user.phone || '',
          password: '',
          password_confirm: '',
          is_active: user.is_active !== undefined ? user.is_active : true,
          is_staff: user.is_staff || false,
          is_accountant: user.is_accountant || false,
          is_cashier: user.is_cashier || false,
          is_developer: user.is_developer || user.has_full_access || false,
          is_superuser: user.is_superuser || false,
          staff_role: user.staff_profile_role || '',
          establishment: '',
          location: '',
          employee_id: '',
          hired_date: new Date().toISOString().split('T')[0],
        });
      } else {
        setFormData({
          username: '',
          email: '',
          first_name: '',
          phone: '',
          password: '',
          password_confirm: '',
          is_active: true,
          is_staff: false,
          is_accountant: false,
          is_cashier: false,
          is_developer: false,
          is_superuser: false,
          staff_role: '',
          establishment: '',
          location: '',
          employee_id: '',
          hired_date: new Date().toISOString().split('T')[0],
        });
      }
    }, [user, open]);

    const handleChange = (field, value) => {
      setFormData(prev => ({ ...prev, [field]: value }));
    };

    // Загрузка филиалов при выборе заведения
    const handleEstablishmentChange = async (establishmentId) => {
      setFormData(prev => ({ ...prev, establishment: establishmentId, location: '' }));
      
      if (establishmentId) {
        try {
          const response = await adminApiService.getEstablishmentLocations(establishmentId);
          setLocations(response.results || response || []);
        } catch (error) {
          console.error('Ошибка загрузки филиалов:', error);
          setLocations([]);
        }
      } else {
        setLocations([]);
      }
    };

    const handleSave = () => {
      // Валидация для создания пользователя
      if (type === 'create') {
        if (!formData.username || !formData.email) {
          alert('Имя пользователя и email обязательны');
          return;
        }
        if (!formData.password) {
          alert('Пароль обязателен для нового пользователя');
          return;
        }
        if (formData.password !== formData.password_confirm) {
          alert('Пароли не совпадают');
          return;
        }
        if (formData.password.length < 8) {
          alert('Пароль должен содержать минимум 8 символов');
          return;
        }
        // Валидация для кассира
        if (formData.staff_role === 'cashier') {
          if (!formData.establishment) {
            alert('Для кассира обязательно выберите заведение');
            return;
          }
          if (!formData.employee_id) {
            alert('Для кассира обязательно укажите табельный номер');
            return;
          }
        }
      }
      
      const dataToSave = { ...formData };
      
      // Удаляем поля подтверждения пароля
      delete dataToSave.password_confirm;
      
      // Для редактирования удаляем пустой пароль
      if (type === 'edit' && !dataToSave.password) {
        delete dataToSave.password;
      }
      
      // Убираем пустые строки, НО НЕ staff_role (может быть пустым намеренно)
      Object.keys(dataToSave).forEach(key => {
        if (dataToSave[key] === '' && key !== 'staff_role') {
          dataToSave[key] = null;
        }
      });
      
      // Если staff_role пустой, отправляем пустую строку (для сброса роли)
      if (!dataToSave.staff_role) {
        dataToSave.staff_role = '';
      }
      
      // Автоматически устанавливаем is_staff при выборе роли
      if (dataToSave.staff_role && dataToSave.staff_role !== '') {
        dataToSave.is_staff = true;
      }
      
      console.log('Данные для сохранения:', dataToSave);
      onSave(dataToSave);
    };

    // Получение роли кассира
    const getCashierRole = (user) => {
      if (!user?.is_cashier) return null;
      
      // Можно добавить логику получения роли из профиля кассира
      // Пока возвращаем базовую роль
      return user.cashier_role || 'cashier';
    };

    const getCashierRoleText = (role) => {
      switch (role) {
        case 'cashier': return 'Кассир';
        case 'senior_cashier': return 'Старший кассир';
        case 'shift_manager': return 'Менеджер смены';
        default: return 'Кассир';
      }
    };

    const getCashierRoleColor = (role) => {
      switch (role) {
        case 'cashier': return 'bg-green-500';
        case 'senior_cashier': return 'bg-blue-500';
        case 'shift_manager': return 'bg-purple-500';
        default: return 'bg-green-500';
      }
    };

    if (!open) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900 flex items-center">
                <FaUserCircle className="w-6 h-6 text-blue-600 mr-3" />
                {type === 'view' ? 'Просмотр пользователя' : 
                 type === 'create' ? 'Создание пользователя' : 'Редактирование пользователя'}
              </h2>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
          
          <div className="p-6 space-y-6">
            {user && type !== 'create' && (
              <div className="flex items-center space-x-4 mb-6 p-4 bg-gray-50 rounded-lg">
                <UserAvatar user={user} size="xl" />
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-gray-900">{user.username}</h3>
                  <p className="text-gray-600 flex items-center mt-1">
                    <FaEnvelope className="w-4 h-4 mr-2" />
                    {user.email}
                  </p>
                  {user.phone && (
                    <p className="text-gray-600 flex items-center mt-1">
                      <FaPhone className="w-4 h-4 mr-2" />
                      {user.phone}
                    </p>
                  )}
                  <div className="mt-2 flex items-center space-x-2">
                    <StatusBadge type={getUserTypeBadge(user)} icon={getUserTypeIcon(user)}>
                      {getUserTypeText(user)}
                    </StatusBadge>
                    {user.is_cashier && (
                      <div className="flex items-center space-x-1">
                        <div className={`w-3 h-3 rounded-full ${getCashierRoleColor(getCashierRole(user))}`}></div>
                        <span className="text-sm text-gray-600">
                          {getCashierRoleText(getCashierRole(user))}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Имя пользователя</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={formData.username}
                  onChange={(e) => handleChange('username', e.target.value)}
                  disabled={type === 'view'}
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <input
                  type="email"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  disabled={type === 'view'}
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Имя</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={formData.first_name}
                  onChange={(e) => handleChange('first_name', e.target.value)}
                  disabled={type === 'view'}
                />
              </div>


              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Телефон</label>
                <input
                  type="tel"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  disabled={type === 'view'}
                  placeholder="+7 (999) 999-99-99"
                />
              </div>

              {type === 'create' && (
                <>
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Пароль <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="password"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      value={formData.password}
                      onChange={(e) => handleChange('password', e.target.value)}
                      placeholder="Минимум 8 символов"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Подтвердите пароль <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="password"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      value={formData.password_confirm}
                      onChange={(e) => handleChange('password_confirm', e.target.value)}
                      placeholder="Повторите пароль"
                    />
                  </div>
                </>
              )}
            </div>

            {(type === 'edit' || type === 'create') && (
              <>
                {/* Активный пользователь */}
                <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                            <FaCheckCircle className="w-5 h-5 text-green-600" />
                          </div>
                          <div>
                            <label htmlFor="is_active" className="text-sm font-semibold text-gray-900 cursor-pointer">
                              Активный пользователь
                            </label>
                            <p className="text-xs text-gray-500 mt-1">Пользователь может входить в систему</p>
                          </div>
                        </div>
                        <div className="relative">
                          <input
                            type="checkbox"
                            id="is_active"
                            checked={formData.is_active}
                            onChange={(e) => handleChange('is_active', e.target.checked)}
                            className="sr-only"
                          />
                          <label
                            htmlFor="is_active"
                            className={`flex items-center cursor-pointer w-12 h-6 rounded-full transition-colors ${
                              formData.is_active ? 'bg-green-500' : 'bg-gray-300'
                            }`}
                          >
                            <span
                              className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform ${
                                formData.is_active ? 'translate-x-6' : 'translate-x-0.5'
                              }`}
                            />
                          </label>
                        </div>
                      </div>
                    </div>

                {/* Роль сотрудника */}
                <div className="space-y-4 p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg border border-indigo-200 shadow-sm">
                  <h4 className="font-semibold text-gray-900 flex items-center">
                    <FaUserTie className="w-4 h-4 mr-2 text-indigo-600" />
                    Роль сотрудника
                  </h4>
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Выберите роль сотрудника
                            </label>
                      <select
                        value={formData.staff_role}
                        onChange={(e) => handleChange('staff_role', e.target.value)}
                        className="w-full px-3 py-2 border border-indigo-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors bg-white"
                        disabled={type === 'view' || !canEditUserRole(user)}
                      >
                        <option value="">Не выбрано</option>
                        {getAvailableRoles().map(role => (
                          <option key={role.value} value={role.value}>
                            {role.label}
                          </option>
                        ))}
                      </select>
                            <p className="text-xs text-gray-500 mt-1">
                        {!canEditUserRole(user) ? (
                          <span className="text-red-600 font-medium">🔒 Вы не можете изменить роль этого пользователя (его роль выше или равна вашей)</span>
                        ) : getAvailableRoles().length === 0 ? (
                          <span className="text-amber-600 font-medium">⚠️ У вас нет прав для назначения ролей</span>
                        ) : (
                          <>Роль определяет уровень доступа. Вы можете назначать только роли ниже своей.</>
                        )}
                      </p>
                      {formData.staff_role && (
                        <div className="mt-3 p-3 bg-white rounded-lg border border-indigo-200">
                          <p className="text-sm text-gray-700">
                            <strong>Описание роли:</strong>
                            {formData.staff_role === 'developer' && ' Максимальные права, доступ ко всем функциям системы'}
                            {formData.staff_role === 'admin' && ' Может управлять пользователями, не может выдавать роли выше своей'}
                            {formData.staff_role === 'manager' && ' Управление заведением, заказами и персоналом'}
                            {formData.staff_role === 'waiter' && ' Прием заказов, обслуживание столов'}
                            {formData.staff_role === 'chef' && ' Просмотр и обработка заказов на кухне'}
                            {formData.staff_role === 'accountant' && ' Доступ к бухгалтерской панели и отчетам'}
                            {formData.staff_role === 'cashier' && ' Работа с кассовой системой'}
                          </p>
                        </div>
                      )}
                      
                      {/* Дополнительные поля для кассира */}
                      {formData.staff_role === 'cashier' && type === 'create' && (
                        <div className="mt-4 space-y-4 p-4 bg-green-50 rounded-lg border border-green-200">
                          <h5 className="font-semibold text-gray-900 text-sm flex items-center">
                            <FaCashRegister className="w-4 h-4 mr-2 text-green-600" />
                            Настройки кассира
                          </h5>
                          
                          {/* Заведение */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Заведение *
                            </label>
                            <select
                              value={formData.establishment}
                              onChange={(e) => handleEstablishmentChange(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                            >
                              <option value="">Выберите заведение</option>
                              {Array.isArray(establishments) && establishments.map((est) => (
                                <option key={est.id} value={est.id}>
                                  {est.name}
                                </option>
                              ))}
                            </select>
                          </div>
                          
                          {/* Филиал */}
                          {formData.establishment && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Филиал (необязательно)
                              </label>
                              <select
                                value={formData.location}
                                onChange={(e) => handleChange('location', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                              >
                                <option value="">Без привязки к филиалу</option>
                                {locations.map((loc) => (
                                  <option key={loc.id} value={loc.id}>
                                    {loc.name}
                                  </option>
                                ))}
                              </select>
                            </div>
                          )}
                          
                          {/* Табельный номер */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Табельный номер *
                            </label>
                            <input
                              type="text"
                              value={formData.employee_id}
                              onChange={(e) => handleChange('employee_id', e.target.value)}
                              placeholder="Например: CASH001"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                            />
                          </div>
                          
                          {/* Дата найма */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Дата найма
                            </label>
                            <input
                              type="date"
                              value={formData.hired_date}
                              onChange={(e) => handleChange('hired_date', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

              </>
            )}

          {user && (
            <>
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <FaCalendarAlt className="w-4 h-4 mr-2" />
                    Основная информация
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Дата регистрации:</span>
                      <div className="font-medium">{formatDate(user.date_joined)}</div>
                    </div>
                    <div>
                      <span className="text-gray-500">Последний вход:</span>
                      <div className="font-medium">
                        {user.last_login ? formatDate(user.last_login) : 'Никогда'}
                      </div>
                    </div>
                    {user.bonus_points !== undefined && (
                      <div>
                        <span className="text-gray-500">Бонусные баллы:</span>
                        <div className="font-medium flex items-center">
                          <FaRubleSign className="w-3 h-3 mr-1" />
                          {user.bonus_points}
                        </div>
                      </div>
                    )}
                    {user.category_data && (
                      <div>
                        <span className="text-gray-500">Категория:</span>
                        <div className="font-medium" style={{ color: user.category_data.color }}>
                          {user.category_data.name} ({user.category_data.discount_percentage}%)
                        </div>
                      </div>
                    )}
                    {user.orders_count !== undefined && (
                      <div>
                        <span className="text-gray-500">Заказов:</span>
                        <div className="font-medium flex items-center">
                          <FaShoppingCart className="w-3 h-3 mr-1" />
                          {user.orders_count}
                        </div>
                      </div>
                    )}
                    {user.total_spent !== undefined && (
                      <div>
                        <span className="text-gray-500">Потрачено:</span>
                        <div className="font-medium flex items-center">
                          <FaRubleSign className="w-3 h-3 mr-1" />
                          {user.total_spent.toLocaleString('ru-RU')}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {user.addresses && user.addresses.length > 0 && (
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                      <FaHome className="w-4 h-4 mr-2" />
                      Адреса ({user.addresses.length})
                    </h4>
                    <div className="space-y-2">
                      {user.addresses.slice(0, 3).map((address, index) => (
                        <div key={index} className="text-sm">
                          <span className="font-medium">{address.title}</span>
                          {address.is_default && (
                            <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                              По умолчанию
                            </span>
                          )}
                          <div className="text-gray-600">{address.address}</div>
                        </div>
                      ))}
                      {user.addresses.length > 3 && (
                        <div className="text-sm text-gray-500">
                          И еще {user.addresses.length - 3} адресов...
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {user.payment_methods && user.payment_methods.length > 0 && (
                  <div className="bg-green-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                      <FaCreditCard className="w-4 h-4 mr-2" />
                      Способы оплаты ({user.payment_methods.length})
                    </h4>
                    <div className="space-y-2">
                      {user.payment_methods.map((method, index) => (
                        <div key={index} className="text-sm flex items-center">
                          <span className="font-medium">{method.type}</span>
                          <span className="ml-2">**** {method.last4}</span>
                          {method.is_default && (
                            <span className="ml-2 px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                              По умолчанию
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {user.favorite_establishments && user.favorite_establishments.length > 0 && (
                  <div className="bg-pink-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                      <FaHeart className="w-4 h-4 mr-2" />
                      Избранные заведения ({user.favorite_establishments.length})
                    </h4>
                    <div className="space-y-2">
                      {user.favorite_establishments.map((establishment, index) => (
                        <div key={index} className="text-sm flex items-center">
                          {establishment.logo && (
                            <img 
                              src={establishment.logo} 
                              alt={establishment.name}
                              className="w-6 h-6 rounded mr-2"
                            />
                          )}
                          <span className="font-medium">{establishment.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {user.groups_list && user.groups_list.length > 0 && (
                  <div className="bg-purple-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                      <FaUserCog className="w-4 h-4 mr-2" />
                      Группы пользователей ({user.groups_list.length})
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {user.groups_list.map((group, index) => (
                        <span 
                          key={index}
                          className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded"
                        >
                          {group.name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {user.user_activities && user.user_activities.length > 0 && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                      <FaHistory className="w-4 h-4 mr-2" />
                      Последняя активность
                    </h4>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {user.user_activities.slice(0, 5).map((activity, index) => (
                        <div key={index} className="text-sm border-l-2 border-blue-200 pl-3">
                          <div className="font-medium">{activity.action}</div>
                          <div className="text-gray-500 text-xs">
                            {formatDate(activity.created_at)} • {activity.ip_address}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
            </>
          )}
          </div>

          <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
            <button 
              onClick={onClose} 
              className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Отмена
            </button>
            {(type === 'edit' || type === 'create') && (
              <button 
                onClick={handleSave} 
                className={`px-4 py-2 text-white rounded-lg transition-colors ${
                  type === 'create' 
                    ? 'bg-green-600 hover:bg-green-700' 
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {type === 'create' ? 'Создать пользователя' : 'Сохранить изменения'}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Уведомления */}
      {notification.show && (
        <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg ${
          notification.type === 'error' ? 'bg-red-100 border border-red-200 text-red-800' :
          'bg-green-100 border border-green-200 text-green-800'
        }`}>
          {notification.message}
        </div>
      )}

      {/* Заголовок */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center">
            <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl flex items-center justify-center mr-4 shadow-lg">
              <FaUsers className="w-5 h-5 text-white" />
            </div>
            Управление пользователями
          </h1>
          <p className="text-gray-600 ml-14">Просмотр и управление пользователями системы</p>
        </div>
        <div className="flex gap-3">
          <Tooltip text="Экспортировать список пользователей в Excel">
            <button 
              onClick={handleExportUsers}
              className="flex items-center gap-2 px-5 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 shadow-sm"
            >
              <FaFileExport className="w-4 h-4" />
              Экспорт
            </button>
          </Tooltip>
          <Tooltip text="Обновить данные пользователей и статистику">
            <button 
              className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all duration-200 shadow-lg hover:shadow-xl"
              onClick={() => {
                loadUsers();
                loadStats();
              }}
            >
              <FaSync className="w-4 h-4" />
              Обновить
            </button>
          </Tooltip>
        </div>
      </div>

      {/* Статистика */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg flex items-center justify-center mr-3">
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            Статистика пользователей
          </h2>
          <div className="text-sm text-gray-500">
            Обновлено: {new Date().toLocaleTimeString('ru-RU')}
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Tooltip text="Общее количество зарегистрированных пользователей в системе">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 border border-blue-200 hover:shadow-md transition-all duration-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-blue-700 mb-1">Всего пользователей</p>
                  <p className="text-3xl font-bold text-blue-900">{stats.total}</p>
                  <p className="text-xs text-blue-600 mt-1">Зарегистрировано</p>
                </div>
                <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                  <FaUsers className="w-6 h-6 text-white" />
                </div>
              </div>
            </div>
          </Tooltip>

          <Tooltip text="Пользователи с активным статусом, которые могут входить в систему">
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 border border-green-200 hover:shadow-md transition-all duration-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-green-700 mb-1">Активные</p>
                  <p className="text-3xl font-bold text-green-900">{stats.active}</p>
                  <p className="text-xs text-green-600 mt-1">Могут входить</p>
                </div>
                <div className="w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center shadow-lg">
                  <FaUserCheck className="w-6 h-6 text-white" />
                </div>
              </div>
            </div>
          </Tooltip>

          <Tooltip text="Заблокированные пользователи без доступа к системе">
            <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-6 border border-red-200 hover:shadow-md transition-all duration-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-red-700 mb-1">Заблокированные</p>
                  <p className="text-3xl font-bold text-red-900">{stats.inactive}</p>
                  <p className="text-xs text-red-600 mt-1">Доступ запрещен</p>
                </div>
                <div className="w-12 h-12 bg-red-600 rounded-xl flex items-center justify-center shadow-lg">
                  <FaUserTimes className="w-6 h-6 text-white" />
                </div>
              </div>
            </div>
          </Tooltip>

          <Tooltip text="Пользователи с правами администратора и доступом к админ-панели">
            <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl p-6 border border-yellow-200 hover:shadow-md transition-all duration-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-yellow-700 mb-1">Администраторы</p>
                  <p className="text-3xl font-bold text-yellow-900">{stats.staff}</p>
                  <p className="text-xs text-yellow-600 mt-1">Админ доступ</p>
                </div>
                <div className="w-12 h-12 bg-yellow-600 rounded-xl flex items-center justify-center shadow-lg">
                  <FaUserTie className="w-6 h-6 text-white" />
                </div>
              </div>
            </div>
          </Tooltip>
        </div>
      </div>

      {/* Поиск и фильтры удалены по требованию — оставляем только переключатель вкладок */}

      {/* Вкладки: Пользователи / Сотрудники */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
        <div className="px-8 pt-6 pb-0 border-b border-gray-100">
          <div className="flex space-x-1 border-b border-gray-200">
            <button
              onClick={() => setActiveTab('customers')}
              className={`px-6 py-3 font-medium text-sm transition-all duration-200 border-b-2 ${
                activeTab === 'customers'
                  ? 'text-blue-600 border-blue-600 bg-blue-50'
                  : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <FaUser className="w-4 h-4" />
                <span>Пользователи (Клиенты)</span>
                <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                  activeTab === 'customers' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                }`}>
                  {users.filter(u => !u.is_staff).length}
                </span>
              </div>
            </button>
            
            <button
              onClick={() => setActiveTab('staff')}
              className={`px-6 py-3 font-medium text-sm transition-all duration-200 border-b-2 ${
                activeTab === 'staff'
                  ? 'text-purple-600 border-purple-600 bg-purple-50'
                  : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <FaUserTie className="w-4 h-4" />
                <span>Сотрудники</span>
                <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                  activeTab === 'staff' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'
                }`}>
                  {users.filter(u => u.is_staff || u.is_cashier || u.is_accountant || u.staff_profile_role).length}
                </span>
              </div>
            </button>
          </div>
        </div>
        
        <div className="px-8 py-6 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <div className={`w-6 h-6 bg-gradient-to-r ${
              activeTab === 'customers' ? 'from-blue-500 to-blue-600' : 'from-purple-500 to-purple-600'
            } rounded-lg flex items-center justify-center mr-3`}>
              <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
              </svg>
            </div>
            {activeTab === 'customers' ? 'Список пользователей (клиентов)' : 'Список сотрудников'}
            <span className={`ml-3 px-3 py-1 ${
              activeTab === 'customers' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
            } text-sm font-medium rounded-full`}>
              {users.filter(u => {
                const isStaff = u.is_staff || u.is_cashier || u.is_accountant || u.staff_profile_role;
                return activeTab === 'customers' ? !isStaff : isStaff;
              }).length}
            </span>
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Пользователь</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Контакты</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Тип</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Статус</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Регистрация</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Действия</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users
                .filter(user => {
                  const isStaff = user.is_staff || user.is_cashier || user.is_accountant || user.staff_profile_role;
                  return activeTab === 'customers' ? !isStaff : isStaff;
                })
                .map((user) => (
                <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <UserAvatar user={user} size="md" />
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{user.username}</div>
                        {user.first_name && (
                        <div className="text-sm text-gray-500">
                            {user.first_name}
                        </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 flex items-center">
                      <FaEnvelope className="w-3 h-3 mr-2 text-gray-400" />
                      {user.email}
                    </div>
                    {user.phone && (
                      <div className="text-sm text-gray-500 flex items-center mt-1">
                        <FaPhone className="w-3 h-3 mr-2 text-gray-400" />
                        {user.phone}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <StatusBadge type={getUserTypeBadge(user)} icon={getUserTypeIcon(user)}>
                        {getUserTypeText(user)}
                      </StatusBadge>
                      {user.is_cashier && (
                        <div className="flex items-center space-x-1">
                          <div className={`w-3 h-3 rounded-full ${
                            user.cashier_role === 'senior_cashier' ? 'bg-blue-500' :
                            user.cashier_role === 'shift_manager' ? 'bg-purple-500' :
                            'bg-green-500'
                          }`}></div>
                          <span className="text-xs text-gray-600">
                            {user.cashier_role === 'senior_cashier' ? 'Старший' :
                             user.cashier_role === 'shift_manager' ? 'Менеджер' :
                             'Кассир'}
                          </span>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <StatusBadge 
                      type={user.is_active ? 'success' : 'danger'} 
                      icon={user.is_active ? FaCheckCircle : FaBan}
                    >
                      {user.is_active ? 'Активен' : 'Заблокирован'}
                    </StatusBadge>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{formatDate(user.date_joined)}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-1">
                      <Tooltip text="Просмотр профиля пользователя">
                        <button
                          onClick={() => handleViewUser(user)}
                          className="text-blue-600 hover:text-blue-900 p-2 hover:bg-blue-50 rounded-lg transition-colors"
                        >
                          <FaEye className="w-4 h-4" />
                        </button>
                      </Tooltip>
                      <Tooltip text={user.is_cashier ? 'Снять права кассира' : 'Назначить кассиром'}>
                        <button
                          onClick={() => handleToggleCashier(user)}
                          className={`p-2 rounded-lg transition-colors ${
                            user.is_cashier 
                              ? 'text-green-600 hover:text-green-900 hover:bg-green-50' 
                              : 'text-teal-600 hover:text-teal-900 hover:bg-teal-50'
                          }`}
                        >
                          <FaCashRegister className="w-4 h-4" />
                        </button>
                      </Tooltip>
                      {user.is_cashier && (
                        <Tooltip text="Редактировать профиль кассира">
                          <button
                            onClick={() => handleEditCashier(user)}
                            className="text-blue-600 hover:text-blue-900 p-2 hover:bg-blue-50 rounded-lg transition-colors"
                          >
                            <FaUserCog className="w-4 h-4" />
                          </button>
                        </Tooltip>
                      )}
                      <Tooltip text="Редактировать данные пользователя">
                        <button
                          onClick={() => handleEditUser(user)}
                          className="text-green-600 hover:text-green-900 p-2 hover:bg-green-50 rounded-lg transition-colors"
                        >
                          <FaEdit className="w-4 h-4" />
                        </button>
                      </Tooltip>
                      <Tooltip text="Сбросить пароль пользователя">
                        <button
                          onClick={() => {
                            setSelectedUserForAction(user);
                            setShowPasswordDialog(true);
                          }}
                          className="text-orange-600 hover:text-orange-900 p-2 hover:bg-orange-50 rounded-lg transition-colors"
                        >
                          <FaKey className="w-4 h-4" />
                        </button>
                      </Tooltip>
                      <Tooltip text={user.is_staff ? 'Снять права администратора' : 'Назначить администратором'}>
                        <button
                          onClick={() => handleToggleStaff(user)}
                          className={`p-2 rounded-lg transition-colors ${
                            user.is_staff 
                              ? 'text-yellow-600 hover:text-yellow-900 hover:bg-yellow-50' 
                              : 'text-purple-600 hover:text-purple-900 hover:bg-purple-50'
                          }`}
                        >
                          <FaUserShield className="w-4 h-4" />
                        </button>
                      </Tooltip>
                      <Tooltip text={user.is_active ? 'Заблокировать пользователя' : 'Активировать пользователя'}>
                        <button
                          onClick={() => handleToggleUserStatus(user)}
                          className={`p-2 rounded-lg transition-colors ${
                            user.is_active 
                              ? 'text-red-600 hover:text-red-900 hover:bg-red-50' 
                              : 'text-green-600 hover:text-green-900 hover:bg-green-50'
                          }`}
                        >
                          {user.is_active ? <FaLock className="w-4 h-4" /> : <FaUnlock className="w-4 h-4" />}
                        </button>
                      </Tooltip>
                      <Tooltip text="Удалить пользователя навсегда">
                        <button
                          onClick={() => {
                            setSelectedUserForAction(user);
                            setShowDeleteDialog(true);
                          }}
                          className="text-red-600 hover:text-red-900 p-2 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <FaTrash className="w-4 h-4" />
                        </button>
                      </Tooltip>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {users.length === 0 && (
          <div className="text-center py-12">
            <FaUsers className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Пользователи не найдены</h3>
            <p className="text-gray-500">Попробуйте изменить параметры поиска или добавьте нового пользователя</p>
          </div>
        )}
      </div>

      {/* Диалог пользователя */}
      <UserDialog
        open={openDialog || showCreateDialog}
        onClose={() => {
          setOpenDialog(false);
          setShowCreateDialog(false);
        }}
        user={selectedUser}
        type={dialogType}
        onSave={handleSaveUser}
        establishments={establishments}
      />

      {/* Диалог сброса пароля */}
      {showPasswordDialog && (
        <PasswordResetDialog
          open={showPasswordDialog}
          onClose={() => {
            setShowPasswordDialog(false);
            setSelectedUserForAction(null);
          }}
          user={selectedUserForAction}
          onSave={handleResetPassword}
        />
      )}

      {/* Диалог удаления пользователя */}
      {showDeleteDialog && (
        <DeleteUserDialog
          open={showDeleteDialog}
          onClose={() => {
            setShowDeleteDialog(false);
            setSelectedUserForAction(null);
          }}
          user={selectedUserForAction}
          onConfirm={handleDeleteUser}
        />
      )}

      {/* Диалог настроек кассира */}
      {showCashierSettingsDialog && (
        <CashierSettingsDialog
          open={showCashierSettingsDialog}
          onClose={() => {
            setShowCashierSettingsDialog(false);
            setSelectedUserForAction(null);
          }}
          user={selectedUserForAction}
          onSave={handleSaveCashierSettings}
          establishments={establishments}
        />
      )}

      {/* Диалог редактирования кассира */}
      {showCashierEditDialog && (
        <CashierEditDialog
          open={showCashierEditDialog}
          onClose={() => {
            setShowCashierEditDialog(false);
            setSelectedUserForAction(null);
          }}
          user={selectedUserForAction}
          onSave={handleUpdateCashierSettings}
          establishments={establishments}
        />
      )}
    </div>
  );
};

// Компонент диалога сброса пароля
const PasswordResetDialog = ({ open, onClose, user, onSave }) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const handleSave = () => {
    if (newPassword !== confirmPassword) {
      alert('Пароли не совпадают');
      return;
    }
    if (newPassword.length < 8) {
      alert('Пароль должен содержать минимум 8 символов');
      return;
    }
    onSave(newPassword);
    setNewPassword('');
    setConfirmPassword('');
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900 flex items-center">
            <FaKey className="w-5 h-5 text-orange-600 mr-3" />
            Сброс пароля
          </h2>
          <p className="text-gray-600 mt-2">
            Сброс пароля для пользователя: <strong>{user?.username}</strong>
          </p>
        </div>
        
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Новый пароль
            </label>
            <input
              type="password"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Минимум 8 символов"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Подтвердите пароль
            </label>
            <input
              type="password"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Повторите пароль"
            />
          </div>
        </div>

        <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
          <button 
            onClick={onClose} 
            className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Отмена
          </button>
          <button 
            onClick={handleSave} 
            className="px-4 py-2 bg-orange-600 text-white hover:bg-orange-700 rounded-lg transition-colors"
            disabled={!newPassword || !confirmPassword}
          >
            Сбросить пароль
          </button>
        </div>
      </div>
    </div>
  );
};

// Компонент диалога удаления пользователя
const DeleteUserDialog = ({ open, onClose, user, onConfirm }) => {
  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-red-900 flex items-center">
            <FaTrash className="w-5 h-5 text-red-600 mr-3" />
            Удаление пользователя
          </h2>
        </div>
        
        <div className="p-6">
          <p className="text-gray-700 mb-4">
            Вы уверены, что хотите удалить пользователя <strong>{user?.username}</strong>?
          </p>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800 text-sm">
              <strong>Внимание!</strong> Это действие нельзя отменить. Все данные пользователя будут удалены безвозвратно.
            </p>
          </div>
        </div>

        <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
          <button 
            onClick={onClose} 
            className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Отмена
          </button>
          <button 
            onClick={onConfirm} 
            className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 rounded-lg transition-colors"
          >
            Удалить пользователя
          </button>
        </div>
      </div>
    </div>
  );
};

// Компонент диалога настроек кассира
const CashierSettingsDialog = ({ open, onClose, user, onSave, establishments = [] }) => {
  const [formData, setFormData] = useState({
    establishment: '',
    location: '',
    employee_id: '',
    role: 'cashier',
    is_active: true,
    can_open_shift: true,
    can_close_shift: true,
    can_refund: false,
    can_discount: false,
    max_discount_percent: 5,
    hired_date: new Date().toISOString().split('T')[0]
  });
  
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);

  // Логирование для отладки
  useEffect(() => {
    if (open) {
      console.log('🔍 CashierSettingsDialog открыт');
      console.log('👤 Пользователь:', user);
      console.log('🏢 Заведения (тип):', typeof establishments);
      console.log('🏢 Заведения (массив?):', Array.isArray(establishments));
      console.log('🏢 Заведения (данные):', establishments);
      console.log('📊 Количество заведений:', Array.isArray(establishments) ? establishments.length : 'НЕ МАССИВ');
      
      // Проверка каждого заведения
      if (Array.isArray(establishments)) {
        establishments.forEach((est, i) => {
          console.log(`  Заведение ${i + 1}:`, est?.name, `(id=${est?.id})`);
        });
      }
    }
  }, [open, user, establishments]);

  // Сброс формы при открытии
  useEffect(() => {
    if (open && user) {
      setFormData({
        establishment: '',
        location: '',
        employee_id: '',
        role: 'cashier',
        is_active: true,
        can_open_shift: true,
        can_close_shift: true,
        can_refund: false,
        can_discount: false,
        max_discount_percent: 5,
        hired_date: new Date().toISOString().split('T')[0]
      });
      setLocations([]);
    }
  }, [open, user]);

  // Загрузка филиалов при выборе заведения
  const handleEstablishmentChange = async (establishmentId) => {
    setFormData(prev => ({ ...prev, establishment: establishmentId, location: '' }));
    
    if (establishmentId) {
      try {
        setLoading(true);
        const response = await adminApiService.getEstablishmentLocations(establishmentId);
        setLocations(response.results || response || []);
      } catch (error) {
        console.error('Ошибка загрузки филиалов:', error);
        setLocations([]);
      } finally {
        setLoading(false);
      }
    } else {
      setLocations([]);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    // Валидация
    if (!formData.establishment) {
      alert('Выберите заведение');
      return;
    }
    // ✅ ИСПРАВЛЕНО: location необязательное поле (может быть null)
    // if (!formData.location) {
    //   alert('Выберите филиал');
    //   return;
    // }
    if (!formData.employee_id) {
      alert('Укажите табельный номер');
      return;
    }

    // Преобразуем пустую строку в null для location
    const dataToSave = {
      ...formData,
      location: formData.location || null
    };

    console.log('💾 Сохранение данных кассира:', dataToSave);
    onSave(dataToSave);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900 flex items-center">
            <FaCashRegister className="w-5 h-5 text-green-600 mr-3" />
            Настройки кассира: {user?.username}
          </h2>
        </div>
        
        <div className="p-6 space-y-6">
          {/* Основные настройки */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Основные настройки</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Заведение <span className="text-red-500">*</span>
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  value={formData.establishment}
                  onChange={(e) => handleEstablishmentChange(e.target.value)}
                >
                  <option value="">Выберите заведение</option>
                  {Array.isArray(establishments) && establishments.length > 0 ? (
                    establishments.map((est) => (
                      <option key={est.id} value={est.id}>
                        {est.name}
                      </option>
                    ))
                  ) : (
                    <option disabled>Заведения не загружены</option>
                  )}
                </select>
                {(!Array.isArray(establishments) || establishments.length === 0) && (
                  <p className="mt-1 text-sm text-red-600">
                    ⚠️ Заведения не загружены. Откройте /force-reload.html
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Филиал <span className="text-gray-400 text-xs">(необязательно)</span>
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  value={formData.location}
                  onChange={(e) => handleChange('location', e.target.value)}
                  disabled={!formData.establishment || loading}
                >
                  <option value="">
                    {loading ? 'Загрузка...' : 'Выберите филиал (или оставьте пустым)'}
                  </option>
                  {locations.map((location) => (
                    <option key={location.id} value={location.id}>
                      {location.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Табельный номер <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  value={formData.employee_id}
                  onChange={(e) => handleChange('employee_id', e.target.value)}
                  placeholder="Например: CASH001"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Роль</label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  value={formData.role}
                  onChange={(e) => handleChange('role', e.target.value)}
                >
                  <option value="cashier">Кассир</option>
                  <option value="senior_cashier">Старший кассир</option>
                  <option value="shift_manager">Менеджер смены</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Дата найма</label>
              <input
                type="date"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                value={formData.hired_date}
                onChange={(e) => handleChange('hired_date', e.target.value)}
              />
            </div>
          </div>

          {/* Статус и права */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Статус и права</h3>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                <div>
                  <label className="text-sm font-medium text-gray-900">Активен</label>
                  <p className="text-xs text-gray-600">Кассир может работать в системе</p>
                </div>
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => handleChange('is_active', e.target.checked)}
                  className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
                />
              </div>

              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div>
                  <label className="text-sm font-medium text-gray-900">Может открывать смены</label>
                  <p className="text-xs text-gray-600">Право на открытие кассовых смен</p>
                </div>
                <input
                  type="checkbox"
                  checked={formData.can_open_shift}
                  onChange={(e) => handleChange('can_open_shift', e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                />
              </div>

              <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg border border-purple-200">
                <div>
                  <label className="text-sm font-medium text-gray-900">Может закрывать смены</label>
                  <p className="text-xs text-gray-600">Право на закрытие кассовых смен</p>
                </div>
                <input
                  type="checkbox"
                  checked={formData.can_close_shift}
                  onChange={(e) => handleChange('can_close_shift', e.target.checked)}
                  className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                />
              </div>

              <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg border border-orange-200">
                <div>
                  <label className="text-sm font-medium text-gray-900">Может делать возвраты</label>
                  <p className="text-xs text-gray-600">Право на возврат товаров</p>
                </div>
                <input
                  type="checkbox"
                  checked={formData.can_refund}
                  onChange={(e) => handleChange('can_refund', e.target.checked)}
                  className="w-4 h-4 text-orange-600 rounded focus:ring-orange-500"
                />
              </div>

              <div className="space-y-3 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-900">Может давать скидки</label>
                    <p className="text-xs text-gray-600">Право на предоставление скидок</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={formData.can_discount}
                    onChange={(e) => handleChange('can_discount', e.target.checked)}
                    className="w-4 h-4 text-yellow-600 rounded focus:ring-yellow-500"
                  />
                </div>
                
                {formData.can_discount && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Максимальный процент скидки
                    </label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="number"
                        min="0"
                        max="100"
                        className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                        value={formData.max_discount_percent}
                        onChange={(e) => handleChange('max_discount_percent', parseFloat(e.target.value) || 0)}
                      />
                      <span className="text-sm text-gray-600">%</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
          <button 
            onClick={onClose} 
            className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Отмена
          </button>
          <button 
            onClick={handleSave} 
            className="px-4 py-2 bg-green-600 text-white hover:bg-green-700 rounded-lg transition-colors"
          >
            Назначить кассиром
          </button>
        </div>
      </div>
    </div>
  );
};

// Компонент диалога редактирования кассира
const CashierEditDialog = ({ open, onClose, user, onSave, establishments = [] }) => {
  const [formData, setFormData] = useState({
    establishment: '',
    location: '',
    employee_id: '',
    role: 'cashier',
    is_active: true,
    can_open_shift: true,
    can_close_shift: true,
    can_refund: false,
    can_discount: false,
    max_discount_percent: 5,
    hired_date: new Date().toISOString().split('T')[0]
  });
  
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);

  // Сброс формы при открытии
  useEffect(() => {
    if (open && user && user.cashierProfile) {
      const profile = user.cashierProfile;
      setFormData({
        establishment: profile.establishment || '',
        location: profile.location || '',
        employee_id: profile.employee_id || '',
        role: profile.role || 'cashier',
        is_active: profile.is_active !== undefined ? profile.is_active : true,
        can_open_shift: profile.can_open_shift !== undefined ? profile.can_open_shift : true,
        can_close_shift: profile.can_close_shift !== undefined ? profile.can_close_shift : true,
        can_refund: profile.can_refund !== undefined ? profile.can_refund : false,
        can_discount: profile.can_discount !== undefined ? profile.can_discount : false,
        max_discount_percent: profile.max_discount_percent || 5,
        hired_date: profile.hired_date || new Date().toISOString().split('T')[0]
      });
      
      // Загружаем филиалы для текущего заведения
      if (profile.establishment) {
        handleEstablishmentChange(profile.establishment);
      }
    }
  }, [open, user]);

  // Загрузка филиалов при выборе заведения
  const handleEstablishmentChange = async (establishmentId) => {
    setFormData(prev => ({ ...prev, establishment: establishmentId, location: '' }));
    
    if (establishmentId) {
      try {
        setLoading(true);
        const response = await adminApiService.getEstablishmentLocations(establishmentId);
        setLocations(response.results || response || []);
      } catch (error) {
        console.error('Ошибка загрузки филиалов:', error);
        setLocations([]);
      } finally {
        setLoading(false);
      }
    } else {
      setLocations([]);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    // Валидация
    if (!formData.establishment) {
      alert('Выберите заведение');
      return;
    }
    if (!formData.location) {
      alert('Выберите филиал');
      return;
    }
    if (!formData.employee_id) {
      alert('Укажите табельный номер');
      return;
    }

    onSave(formData);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900 flex items-center">
            <FaCashRegister className="w-5 h-5 text-green-600 mr-3" />
            Редактирование профиля кассира: {user?.username}
          </h2>
        </div>
        
        <div className="p-6 space-y-6">
          {/* Основные настройки */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Основные настройки</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Заведение <span className="text-red-500">*</span>
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  value={formData.establishment}
                  onChange={(e) => handleEstablishmentChange(e.target.value)}
                >
                  <option value="">Выберите заведение</option>
                  {Array.isArray(establishments) && establishments.length > 0 ? (
                    establishments.map((est) => (
                      <option key={est.id} value={est.id}>
                        {est.name}
                      </option>
                    ))
                  ) : (
                    <option disabled>Заведения не загружены</option>
                  )}
                </select>
                {(!Array.isArray(establishments) || establishments.length === 0) && (
                  <p className="mt-1 text-sm text-red-600">
                    ⚠️ Заведения не загружены. Откройте /force-reload.html
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Филиал <span className="text-gray-400 text-xs">(необязательно)</span>
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  value={formData.location}
                  onChange={(e) => handleChange('location', e.target.value)}
                  disabled={!formData.establishment || loading}
                >
                  <option value="">
                    {loading ? 'Загрузка...' : 'Выберите филиал (или оставьте пустым)'}
                  </option>
                  {locations.map((location) => (
                    <option key={location.id} value={location.id}>
                      {location.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Табельный номер <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  value={formData.employee_id}
                  onChange={(e) => handleChange('employee_id', e.target.value)}
                  placeholder="Например: CASH001"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Роль</label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  value={formData.role}
                  onChange={(e) => handleChange('role', e.target.value)}
                >
                  <option value="cashier">Кассир</option>
                  <option value="senior_cashier">Старший кассир</option>
                  <option value="shift_manager">Менеджер смены</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Дата найма</label>
              <input
                type="date"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                value={formData.hired_date}
                onChange={(e) => handleChange('hired_date', e.target.value)}
              />
            </div>
          </div>

          {/* Статус и права */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Статус и права</h3>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                <div>
                  <label className="text-sm font-medium text-gray-900">Активен</label>
                  <p className="text-xs text-gray-600">Кассир может работать в системе</p>
                </div>
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => handleChange('is_active', e.target.checked)}
                  className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
                />
              </div>

              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div>
                  <label className="text-sm font-medium text-gray-900">Может открывать смены</label>
                  <p className="text-xs text-gray-600">Право на открытие кассовых смен</p>
                </div>
                <input
                  type="checkbox"
                  checked={formData.can_open_shift}
                  onChange={(e) => handleChange('can_open_shift', e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                />
              </div>

              <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg border border-purple-200">
                <div>
                  <label className="text-sm font-medium text-gray-900">Может закрывать смены</label>
                  <p className="text-xs text-gray-600">Право на закрытие кассовых смен</p>
                </div>
                <input
                  type="checkbox"
                  checked={formData.can_close_shift}
                  onChange={(e) => handleChange('can_close_shift', e.target.checked)}
                  className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                />
              </div>

              <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg border border-orange-200">
                <div>
                  <label className="text-sm font-medium text-gray-900">Может делать возвраты</label>
                  <p className="text-xs text-gray-600">Право на возврат товаров</p>
                </div>
                <input
                  type="checkbox"
                  checked={formData.can_refund}
                  onChange={(e) => handleChange('can_refund', e.target.checked)}
                  className="w-4 h-4 text-orange-600 rounded focus:ring-orange-500"
                />
              </div>

              <div className="space-y-3 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-900">Может давать скидки</label>
                    <p className="text-xs text-gray-600">Право на предоставление скидок</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={formData.can_discount}
                    onChange={(e) => handleChange('can_discount', e.target.checked)}
                    className="w-4 h-4 text-yellow-600 rounded focus:ring-yellow-500"
                  />
                </div>
                
                {formData.can_discount && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Максимальный процент скидки
                    </label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="number"
                        min="0"
                        max="100"
                        className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                        value={formData.max_discount_percent}
                        onChange={(e) => handleChange('max_discount_percent', parseFloat(e.target.value) || 0)}
                      />
                      <span className="text-sm text-gray-600">%</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
          <button 
            onClick={onClose} 
            className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Отмена
          </button>
          <button 
            onClick={handleSave} 
            className="px-4 py-2 bg-green-600 text-white hover:bg-green-700 rounded-lg transition-colors"
          >
            Сохранить изменения
          </button>
        </div>
      </div>
    </div>
  );
};

export default UsersManagement; 